/**
 * Product bundles: one listing that sells several of the same seller's
 * products together at one price (owner decisions 2026-10-28).
 *
 * A bundle is a one-off product (`pricingModel: 'standard'`) created with
 * `productKind: 'bundle'`, which firestore.rules pins, and `bundle.items`, the
 * products it holds. Only the setBundleItems callable writes `bundle`: owning a
 * bundle grants its items, so the list has to be checked by the server, item by
 * item, before anyone can buy it.
 *
 * What a bundle holds:
 *   - one-off products, and tiered products at the ONE tier the seller picked,
 *     which must be a one-off tier (shown in brackets wherever the item is
 *     listed: "Place Value Games (Classroom pack)");
 *   - free products as extras, which never count toward the minimum;
 *   - never pay what you want, subscriptions, another bundle, or another
 *     seller's product.
 * At most BUNDLE_MAX_ITEMS, and a live bundle needs BUNDLE_MIN_PAID_ITEMS paid
 * ones: below that the server archives it and tells the seller. Bundling is
 * offered only to a seller with two products it could hold.
 *
 * Buyers get every item, and every item the seller adds later; an item the
 * seller removes is never taken back. See functions/src/lib/product-bundles.ts,
 * the authoritative copy this one MIRRORS (product-bundles-mirror.test.ts runs
 * both on the same inputs), for how the grants are recorded.
 *
 * PURE: no React, no Firestore.
 */

import { PRICE_MIN_AUD, PRICE_MAX_AUD } from './price-bounds.ts';

/** The `productKind` a bundle is created with. */
export const BUNDLE_KIND = 'bundle';

/** Most products one bundle may hold. */
export const BUNDLE_MAX_ITEMS = 100;

/** A live bundle needs at least this many paid items; free ones are extras. */
export const BUNDLE_MIN_PAID_ITEMS = 2;

/** Most items one buyer's bundle entitlement may record (grants only accumulate). */
export const BUNDLE_MAX_GRANTS = 1000;

/** One item as the bundle stores it: the product, and the tier for a tiered one. */
export type BundleItemRef = { productId: string; tierId?: string };

/** One item as a buyer holds it, named for receipts and the library. */
export type BundleGrant = {
  productId: string;
  productName: string;
  tierId?: string;
  tierName?: string;
  tierLicence?: 'single' | 'team' | 'school';
  tierLicenceSeats?: number;
};

/** Why a product cannot be (or no longer counts as) an item of a bundle. */
export type BundleItemProblem =
  | 'missing'
  | 'other_seller'
  | 'bundle'
  | 'seller_deleted'
  | 'taken_down'
  | 'not_live'
  | 'pricing'
  | 'tier_required'
  | 'tier_missing'
  | 'tier_recurring';

export type ResolvedBundleItem = {
  ref: BundleItemRef;
  grant: BundleGrant;
  paid: boolean;
  listPriceAud: number;
};

export type BundleResolution = {
  items: ResolvedBundleItem[];
  dropped: Array<{ ref: BundleItemRef; problem: BundleItemProblem }>;
  paidCount: number;
};

type Doc = Record<string, unknown>;
type StoredTier = { id?: unknown; name?: unknown; price?: unknown; purchaseType?: unknown; licence?: unknown; licenceSeats?: unknown };

const MAX_ID_LENGTH = 128;
const MAX_TIER_ID_LENGTH = 64;
const MAX_NAME_LENGTH = 200;
const TEAM_SEATS_MIN = 2;
const TEAM_SEATS_MAX = 100;

function asDoc(value: unknown): Doc | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Doc : null;
}

function cleanProductId(value: unknown): string {
  if (typeof value !== 'string') return '';
  const id = value.trim();
  return id && id.length <= MAX_ID_LENGTH && !id.includes('/') ? id : '';
}

function cleanTierId(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return '';
  const id = value.trim();
  return id.length <= MAX_TIER_ID_LENGTH ? id : '';
}

function cleanName(value: unknown): string {
  return typeof value === 'string' ? value.trim().slice(0, MAX_NAME_LENGTH) : '';
}

function roundAud(value: number): number {
  return Math.round(value * 100) / 100;
}

// The three tier readers below are functions/src/lib/price-tiers.ts's
// findPriceTier, isSubscriptionTier and tierName, and licenceStamp is
// functions/src/lib/tier-licences.ts's tierLicenceStamp, restated so this file
// imports nothing the browser does not already ship.
function findTier(tiers: unknown, tierId: unknown): StoredTier | null {
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  if (tierId === null || tierId === undefined || tierId === '') return null;
  const wanted = String(tierId);
  for (const tier of tiers) {
    if (!tier || typeof tier !== 'object') continue;
    const id = (tier as StoredTier).id;
    if (id === null || id === undefined) continue;
    if (String(id) === wanted) return tier as StoredTier;
  }
  return null;
}

function tierRecurs(tier: StoredTier | null | undefined): boolean {
  return tier?.purchaseType === 'subscription';
}

function tierNameOf(tier: StoredTier | null | undefined): string {
  return typeof tier?.name === 'string' ? tier.name.trim().slice(0, 200) : '';
}

function licenceStamp(source: { licence?: unknown; licenceSeats?: unknown } | null | undefined): Pick<BundleGrant, 'tierLicence' | 'tierLicenceSeats'> | null {
  const kind = source?.licence;
  if (kind !== 'single' && kind !== 'team' && kind !== 'school') return null;
  if (kind !== 'team') return { tierLicence: kind };
  const raw = source?.licenceSeats;
  const seats = typeof raw === 'number' ? raw : Number.NaN;
  if (!Number.isInteger(seats) || seats < TEAM_SEATS_MIN || seats > TEAM_SEATS_MAX) return null;
  return { tierLicence: kind, tierLicenceSeats: seats };
}

/** Whether a product document is a bundle. */
export function isBundleProduct(data: unknown): boolean {
  return asDoc(data)?.productKind === BUNDLE_KIND;
}

/** A stored list of items, cleaned: one per product, first kept, at most BUNDLE_MAX_ITEMS. */
export function normalizeBundleItemRefs(raw: unknown): BundleItemRef[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: BundleItemRef[] = [];
  for (const entry of raw) {
    if (out.length >= BUNDLE_MAX_ITEMS) break;
    const doc = asDoc(entry);
    const productId = cleanProductId(doc?.productId);
    if (!productId || seen.has(productId)) continue;
    seen.add(productId);
    const tierId = cleanTierId(doc?.tierId);
    out.push(tierId ? { productId, tierId } : { productId });
  }
  return out;
}

/** The items a product document bundles, in the seller's order (empty for a non-bundle). */
export function storedBundleItems(data: unknown): BundleItemRef[] {
  const doc = asDoc(data);
  if (!doc || !isBundleProduct(doc)) return [];
  return normalizeBundleItemRefs(asDoc(doc.bundle)?.items);
}

export function bundleItemIds(items: ReadonlyArray<{ productId: string }>): string[] {
  return items.map((item) => item.productId);
}

/**
 * How many products a bundle sells right now, as the server last counted them
 * (`bundle.count`, kept by setBundleItems and onBundleProductWritten), or null
 * when none is stored. The stored list is a wish: an item off sale stops
 * counting until it is back, so this is the number a card may promise.
 */
export function storedBundleCount(data: unknown): number | null {
  const doc = asDoc(data);
  if (!doc || !isBundleProduct(doc)) return null;
  const count = asDoc(doc.bundle)?.count;
  return typeof count === 'number' && Number.isInteger(count) && count >= 0 && count <= BUNDLE_MAX_ITEMS
    ? count
    : null;
}

/**
 * The price an item counts at inside a bundle, in AUD: what it costs on its own
 * right now (a running sale included), the chosen tier's price for a tiered
 * product, 0 for anything free or unpriceable. The "save X%" line compares the
 * bundle's price with the sum of these.
 */
export function bundleItemListPriceAud(item: unknown, ref: BundleItemRef): number {
  const doc = asDoc(item);
  if (!doc) return 0;
  const model = doc.pricingModel;
  if (model === 'free') return 0;
  if (model === 'tiered') {
    const tier = findTier(doc.priceTiers, ref.tierId);
    if (!tier || tierRecurs(tier)) return 0;
    const price = typeof tier.price === 'number' ? tier.price : Number(tier.price);
    return Number.isFinite(price) && price > 0 && price <= PRICE_MAX_AUD ? roundAud(price) : 0;
  }
  if (model !== undefined && model !== null && model !== 'standard') return 0;
  const price = Number(doc.price);
  const discounted = doc.discountedPrice;
  const chosen = discounted !== undefined && discounted !== null && Number(discounted) < price
    ? Number(discounted) : price;
  return Number.isFinite(chosen) && chosen > 0 && chosen <= PRICE_MAX_AUD ? roundAud(chosen) : 0;
}

/** Whether an item counts toward BUNDLE_MIN_PAID_ITEMS (free products never do). */
export function isPaidBundleItem(item: unknown, ref: BundleItemRef): boolean {
  return asDoc(item)?.pricingModel !== 'free' && bundleItemListPriceAud(item, ref) >= PRICE_MIN_AUD;
}

/**
 * Why a product cannot be an item of this seller's bundle, or null when it can.
 * `requireLive: false` skips only the live check, for an item already in a
 * bundle that its seller has archived for now.
 */
export function bundleItemProblem(
  bundleSellerId: string,
  item: unknown,
  ref: BundleItemRef,
  options: { requireLive?: boolean } = {},
): BundleItemProblem | null {
  const doc = asDoc(item);
  if (!doc) return 'missing';
  if (!bundleSellerId || doc.sellerId !== bundleSellerId) return 'other_seller';
  if (isBundleProduct(doc)) return 'bundle';
  if (doc.sellerDeleted === true) return 'seller_deleted';
  if (doc.takedownReason !== undefined && doc.takedownReason !== null && doc.takedownReason !== '') return 'taken_down';
  if (options.requireLive !== false && doc.status !== 'live') return 'not_live';
  const model = doc.pricingModel;
  if (model === 'tiered') {
    if (!ref.tierId) return 'tier_required';
    const tier = findTier(doc.priceTiers, ref.tierId);
    if (!tier) return 'tier_missing';
    if (tierRecurs(tier)) return 'tier_recurring';
    return null;
  }
  if (model === 'pwyw' || model === 'subscription') return 'pricing';
  if (model !== undefined && model !== null && model !== 'standard' && model !== 'free') return 'pricing';
  return null;
}

/** What a buyer of the bundle is granted for one item. */
export function bundleGrantFor(productId: string, item: unknown, ref: BundleItemRef): BundleGrant {
  const doc = asDoc(item) ?? {};
  const grant: BundleGrant = { productId, productName: cleanName(doc.name) };
  if (doc.pricingModel === 'tiered' && ref.tierId) {
    const tier = findTier(doc.priceTiers, ref.tierId);
    grant.tierId = ref.tierId;
    if (tier) {
      const name = tierNameOf(tier);
      if (name) grant.tierName = name;
      const licence = licenceStamp(tier);
      if (licence) {
        grant.tierLicence = licence.tierLicence;
        if (licence.tierLicenceSeats !== undefined) grant.tierLicenceSeats = licence.tierLicenceSeats;
      }
    }
  }
  return grant;
}

/** A bundle's stored items checked against their product documents. */
export function resolveBundleItems(
  bundleSellerId: string,
  refs: readonly BundleItemRef[],
  itemsById: ReadonlyMap<string, unknown>,
): BundleResolution {
  const items: ResolvedBundleItem[] = [];
  const dropped: BundleResolution['dropped'] = [];
  let paidCount = 0;
  for (const ref of refs) {
    const item = itemsById.get(ref.productId);
    const problem = bundleItemProblem(bundleSellerId, item, ref);
    if (problem) {
      dropped.push({ ref, problem });
      continue;
    }
    const paid = isPaidBundleItem(item, ref);
    if (paid) paidCount++;
    items.push({ ref, grant: bundleGrantFor(ref.productId, item, ref), paid, listPriceAud: bundleItemListPriceAud(item, ref) });
  }
  return { items, dropped, paidCount };
}

export function bundleIsSellable(resolution: Pick<BundleResolution, 'paidCount'>): boolean {
  return resolution.paidCount >= BUNDLE_MIN_PAID_ITEMS;
}

/** Grants read back from Firestore: one per product, first kept, re-checked. */
export function normalizeBundleGrants(raw: unknown): BundleGrant[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: BundleGrant[] = [];
  for (const entry of raw) {
    if (out.length >= BUNDLE_MAX_GRANTS) break;
    const doc = asDoc(entry);
    const productId = cleanProductId(doc?.productId);
    if (!doc || !productId || seen.has(productId)) continue;
    seen.add(productId);
    const grant: BundleGrant = { productId, productName: cleanName(doc.productName) };
    const tierId = cleanTierId(doc.tierId);
    if (tierId) {
      grant.tierId = tierId;
      const name = cleanName(doc.tierName);
      if (name) grant.tierName = name;
      const licence = licenceStamp({ licence: doc.tierLicence, licenceSeats: doc.tierLicenceSeats });
      if (licence) {
        grant.tierLicence = licence.tierLicence;
        if (licence.tierLicenceSeats !== undefined) grant.tierLicenceSeats = licence.tierLicenceSeats;
      }
    }
    out.push(grant);
  }
  return out;
}

export function mergeBundleGrants(first: readonly BundleGrant[], extra: readonly BundleGrant[]): BundleGrant[] {
  return normalizeBundleGrants([...first, ...extra]);
}

export function grantForProduct(grants: unknown, productId: string): BundleGrant | null {
  return normalizeBundleGrants(grants).find((grant) => grant.productId === productId) ?? null;
}

/** Every bundle a guest order paid for, with what each grants, minus refunded bundles. */
export function guestOrderBundleGrants(order: unknown): Map<string, BundleGrant[]> {
  const doc = asDoc(order);
  const out = new Map<string, BundleGrant[]>();
  if (!doc) return out;
  const refunded = new Set(Array.isArray(doc.refundedProductIds)
    ? doc.refundedProductIds.filter((id): id is string => typeof id === 'string') : []);
  const later = asDoc(doc.bundleGrants) ?? {};
  const lines = Array.isArray(doc.items) ? doc.items : [];
  for (const line of lines) {
    const lineDoc = asDoc(line);
    const bundleId = cleanProductId(lineDoc?.productId);
    if (!lineDoc || !bundleId || refunded.has(bundleId) || !Array.isArray(lineDoc.bundleItems)) continue;
    out.set(bundleId, mergeBundleGrants(normalizeBundleGrants(lineDoc.bundleItems), normalizeBundleGrants(later[bundleId])));
  }
  return out;
}

/** The bundle of a guest order that grants a product, with the grant, or null. */
export function guestBundleGrantFor(
  order: unknown,
  productId: string,
  claimedIds?: ReadonlySet<string>,
): { bundleId: string; grant: BundleGrant } | null {
  for (const [bundleId, grants] of guestOrderBundleGrants(order)) {
    if (claimedIds && !claimedIds.has(bundleId)) continue;
    const grant = grants.find((entry) => entry.productId === productId);
    if (grant) return { bundleId, grant };
  }
  return null;
}

/**
 * Every tier an entitlement unlocks: its own, then any a bundle added beside it,
 * each with the bundle behind it ('' for a tier bought on its own).
 */
export function entitledTiers(existing: unknown): Array<{ tierId: string; viaBundleId: string }> {
  const doc = asDoc(existing);
  if (!doc) return [];
  const out: Array<{ tierId: string; viaBundleId: string }> = [];
  const own = doc.tierId === undefined || doc.tierId === null ? '' : String(doc.tierId);
  out.push({ tierId: own, viaBundleId: cleanProductId(doc.viaBundleId) });
  const raw = Array.isArray(doc.bundleTierGrants) ? doc.bundleTierGrants : [];
  for (const entry of raw) {
    const grant = asDoc(entry);
    const bundleId = cleanProductId(grant?.bundleId);
    const tierId = cleanTierId(grant?.tierId);
    if (!grant || !bundleId || !tierId) continue;
    if (!out.some((tier) => tier.tierId === tierId)) out.push({ tierId, viaBundleId: bundleId });
  }
  return out;
}

// --- Browser-only helpers below: display and the seller's picker. ---

/** An item's name as every list prints it, the tier in brackets: "Place Value Games (Classroom pack)". */
export function bundleItemLabel(productName: string, tierName?: string | null): string {
  const name = productName.trim() || 'Untitled product';
  const tier = (tierName ?? '').trim();
  return tier ? `${name} (${tier})` : name;
}

/**
 * What buying the items separately would cost against the bundle's price, in
 * AUD, and the saving as a whole percent. Null when the bundle saves nothing,
 * so no "save" line is ever shown for a bundle that costs as much or more.
 */
export function bundleSaving(
  bundlePriceAud: number,
  itemPricesAud: readonly number[],
): { separateAud: number; savingAud: number; percent: number } | null {
  const separateAud = roundAud(itemPricesAud.reduce((sum, price) => sum + (Number.isFinite(price) && price > 0 ? price : 0), 0));
  if (!Number.isFinite(bundlePriceAud) || bundlePriceAud <= 0 || separateAud <= bundlePriceAud) return null;
  const savingAud = roundAud(separateAud - bundlePriceAud);
  const percent = Math.floor((savingAud / separateAud) * 100);
  return percent >= 1 ? { separateAud, savingAud, percent } : null;
}

/** The seller-facing reason a product cannot go in a bundle, for the picker. */
export function bundleItemProblemMessage(problem: BundleItemProblem): string {
  switch (problem) {
    case 'missing': return 'This product no longer exists.';
    case 'other_seller': return 'Only your own products can go in your bundle.';
    case 'bundle': return "A bundle can't go inside another bundle. Add its products instead.";
    case 'seller_deleted': return 'This product belongs to a deleted account.';
    case 'taken_down': return 'This product was taken down, so it cannot be bundled.';
    case 'not_live': return 'Draft: launch it first.';
    case 'pricing': return "Pay what you want and subscription products can't go in a bundle.";
    case 'tier_required': return 'Choose which tier is in the bundle.';
    case 'tier_missing': return 'That tier is no longer on the product. Choose another.';
    case 'tier_recurring': return "Subscription tiers can't go in a bundle. Choose a one-off tier.";
  }
}

/** The one-off tiers of a tiered product a bundle can hold, cheapest first, with their prices in AUD. */
export function bundleableTiers(item: unknown): Array<{ tierId: string; name: string; priceAud: number }> {
  const doc = asDoc(item);
  if (!doc || doc.pricingModel !== 'tiered' || !Array.isArray(doc.priceTiers)) return [];
  const out: Array<{ tierId: string; name: string; priceAud: number }> = [];
  for (const raw of doc.priceTiers) {
    const tier = asDoc(raw) as StoredTier | null;
    if (!tier || tier.id === undefined || tier.id === null || tierRecurs(tier)) continue;
    const tierId = cleanTierId(tier.id);
    if (!tierId) continue;
    out.push({ tierId, name: tierNameOf(tier) || 'Untitled tier', priceAud: bundleItemListPriceAud(doc, { productId: '', tierId }) });
  }
  return out.sort((a, b) => a.priceAud - b.priceAud);
}

/**
 * Whether a product could go in a bundle at all: live, the seller's own, not a
 * bundle, and priced as a one-off (a tiered one needs a one-off tier). Free
 * products can, but `paidOnly` leaves them out, for counting toward the two
 * products bundling needs before it is offered.
 */
export function canBeBundled(item: unknown, sellerId: string, options: { paidOnly?: boolean } = {}): boolean {
  const doc = asDoc(item);
  if (!doc) return false;
  if (doc.pricingModel === 'tiered') {
    const tiers = bundleableTiers(doc);
    const usable = options.paidOnly ? tiers.filter((tier) => tier.priceAud >= PRICE_MIN_AUD) : tiers;
    return usable.length > 0 && bundleItemProblem(sellerId, doc, { productId: '', tierId: usable[0].tierId }) === null;
  }
  if (bundleItemProblem(sellerId, doc, { productId: '' }) !== null) return false;
  return !options.paidOnly || isPaidBundleItem(doc, { productId: '' });
}

/**
 * Whether to offer bundling to this seller: two live products a bundle could
 * hold as paid items. Below that the "New bundle" button is never shown.
 */
export function canOfferBundles(products: Iterable<unknown>, sellerId: string): boolean {
  let count = 0;
  for (const product of products) {
    if (canBeBundled(product, sellerId, { paidOnly: true })) count++;
    if (count >= BUNDLE_MIN_PAID_ITEMS) return true;
  }
  return false;
}

/**
 * What every bundle the buyer owns grants for one product: the bundle that
 * granted it, and any that added its tier beside a purchase of its own. That
 * added tier's licence is stamped only on the bundle's grant, never on the
 * product's own entitlement. `bundleGrants` maps an owned bundle's id to its
 * grants, as the owned-products context keeps them.
 */
export function ownedBundleGrantsFor(
  bundleGrants: ReadonlyMap<string, readonly BundleGrant[]>,
  productId: string,
): Array<{ bundleId: string; grant: BundleGrant }> {
  const out: Array<{ bundleId: string; grant: BundleGrant }> = [];
  for (const [bundleId, grants] of bundleGrants) {
    const grant = grantForProduct(grants, productId);
    if (grant) out.push({ bundleId, grant });
  }
  return out;
}

/**
 * How much of a school a licence covers, to choose between two a buyer holds
 * on one product (a tier bought on its own and a licensed tier a bundle
 * added): the whole school, then a team by its seats, then one teacher.
 */
export function licenceReach(licence: { kind: 'single' | 'team' | 'school'; seats: number | null }): number {
  if (licence.kind === 'school') return Number.MAX_SAFE_INTEGER;
  if (licence.kind === 'team') return licence.seats ?? 2;
  return 1;
}

/**
 * The seller's live bundles that hold a product, each with whether taking that
 * product off sale (archiving or deleting it) would leave the bundle with fewer
 * than BUNDLE_MIN_PAID_ITEMS paid products on sale, so the server would archive
 * it too. The products page warns with this before the seller does it.
 */
export function bundlesHolding<P extends { id: string; status?: unknown; sellerId?: unknown }>(
  productId: string,
  sellerProducts: readonly P[],
): Array<{ bundle: P; wouldArchive: boolean }> {
  const byId = new Map<string, unknown>(sellerProducts.map((product) => [product.id, product]));
  const out: Array<{ bundle: P; wouldArchive: boolean }> = [];
  for (const candidate of sellerProducts) {
    if (!isBundleProduct(candidate) || candidate.status !== 'live') continue;
    const refs = storedBundleItems(candidate);
    if (!refs.some((ref) => ref.productId === productId)) continue;
    const without = new Map(byId);
    without.delete(productId);
    const sellerId = typeof candidate.sellerId === 'string' ? candidate.sellerId : '';
    out.push({ bundle: candidate, wouldArchive: resolveBundleItems(sellerId, refs, without).paidCount < BUNDLE_MIN_PAID_ITEMS });
  }
  return out;
}

/**
 * How many products a bundle sells, for the "Bundle of N products" badge on a
 * card and the cart line, or 0 for anything that is not a bundle: the server's
 * count (storedBundleCount), else the length of its list. The marketplace's and
 * the storefront's server projections carry both.
 */
export function bundleSizeOf(data: unknown): number {
  const doc = asDoc(data);
  if (!doc || !isBundleProduct(doc)) return 0;
  const counted = storedBundleCount(doc);
  if (counted !== null) return counted;
  const bundle = asDoc(doc.bundle);
  if (Array.isArray(bundle?.itemIds)) return bundle.itemIds.filter((id) => typeof id === 'string' && id).length;
  return normalizeBundleItemRefs(bundle?.items).length;
}
